/**
 * 🛡️ FatSecretTrustStat — mini-stat affichée sur la page Settings/Profile
 *
 * Yuri spec 2026-02: "87% de tes repas sont vérifiés par FatSecret" — métrique
 * de confiance qui pousse subtilement vers la précision (et vers les produits
 * canadiens). Cyan glow signature cohérente avec le badge FatSecret.
 *
 * Hide gracefully when:
 *  • the API fails (network / missing endpoint)
 *  • the user has < 1 meal logged in the 30-day window (nothing to brag about)
 */
import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { ShieldCheck, TrendingUp } from 'lucide-react';
import { api, useLanguage } from '../context/AppContext';

const LABELS = {
  fr: {
    title: 'Précision FatSecret',
    elite: 'Élite — tu maîtrises la précision',
    strong: 'Solide — la moitié de tes repas est vérifiée',
    warming_up: 'En rodage — vise les produits québécois connus',
    starting: 'Pas assez de repas trackés cette semaine',
    of_meals: 'de tes repas (30 derniers jours)',
    verified: 'vérifiés',
    nudge_verified: 'macros validées contre la base FatSecret Canada (1.5M+ aliments).',
    nudge_starting: 'Tracke 5 repas cette semaine pour activer ta stat de précision.',
  },
  en: {
    title: 'FatSecret precision',
    elite: 'Elite — you master precision',
    strong: 'Solid — half your meals are verified',
    warming_up: 'Warming up — try well-known Canadian products',
    starting: 'Not enough meals logged this week',
    of_meals: 'of your meals (last 30 days)',
    verified: 'verified',
    nudge_verified: 'macros validated against FatSecret Canada (1.5M+ foods).',
    nudge_starting: 'Log 5 meals this week to activate your precision stat.',
  },
};

export default function FatSecretTrustStat() {
  const { language } = useLanguage();
  const fr = (language || 'fr').startsWith('fr');
  const t = fr ? LABELS.fr : LABELS.en;
  const [data, setData] = useState(null);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    let alive = true;
    api.get('/users/fatsecret-stats')
      .then((r) => { if (alive) setData(r.data); })
      .catch(() => {})
      .finally(() => { if (alive) setLoaded(true); });
    return () => { alive = false; };
  }, []);

  if (!loaded || !data) return null;

  const pct = data.verified_pct;
  const isStarting = data.label === 'starting';
  const subtitle = isStarting ? t.starting : t[data.label];

  // Animated number from 0 to pct
  return (
    <motion.div
      initial={{ opacity: 0, y: 6 }}
      animate={{ opacity: 1, y: 0 }}
      className="relative rounded-2xl border border-cyan-400/40 bg-gradient-to-br from-cyan-950/35 via-zinc-950/80 to-blue-950/30 p-4 mb-6 overflow-hidden"
      style={{
        boxShadow: '0 0 18px rgba(34,211,238,0.42), 0 0 6px rgba(59,130,246,0.35), inset 0 0 0 1px rgba(34,211,238,0.12)',
      }}
      data-testid="fatsecret-trust-stat"
    >
      <div className="flex items-center gap-4">
        {/* Big % gauge or starter icon */}
        {!isStarting ? (
          <div className="shrink-0 relative w-20 h-20 flex items-center justify-center">
            <svg width="80" height="80" className="absolute inset-0 -rotate-90">
              <defs>
                <linearGradient id="fs-trust-grad" x1="0%" y1="0%" x2="100%" y2="100%">
                  <stop offset="0%" stopColor="#67e8f9" />
                  <stop offset="100%" stopColor="#3b82f6" />
                </linearGradient>
              </defs>
              <circle cx="40" cy="40" r="32" fill="none" stroke="rgba(255,255,255,0.06)" strokeWidth="6" />
              <circle
                cx="40"
                cy="40"
                r="32"
                fill="none"
                stroke="url(#fs-trust-grad)"
                strokeWidth="6"
                strokeLinecap="round"
                strokeDasharray={2 * Math.PI * 32}
                strokeDashoffset={(2 * Math.PI * 32) * (1 - pct / 100)}
                style={{ transition: 'stroke-dashoffset 1.2s ease-out', filter: 'drop-shadow(0 0 6px rgba(34,211,238,0.7))' }}
              />
            </svg>
            <div className="relative flex flex-col items-center">
              <span className="text-2xl font-black text-cyan-100 leading-none" data-testid="fatsecret-trust-pct">
                {pct}<span className="text-sm">%</span>
              </span>
              <ShieldCheck className="w-3 h-3 text-cyan-300 mt-0.5" />
            </div>
          </div>
        ) : (
          <div
            className="shrink-0 w-20 h-20 rounded-2xl bg-gradient-to-br from-cyan-500/30 to-blue-500/25 ring-1 ring-cyan-400/50 flex items-center justify-center"
            style={{ boxShadow: '0 0 12px rgba(34,211,238,0.55)' }}
          >
            <TrendingUp className="w-9 h-9 text-cyan-200" />
          </div>
        )}

        {/* Text block */}
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-0.5">
            <p className="text-[9px] uppercase tracking-[0.28em] font-bold text-cyan-300">
              {t.title}
            </p>
            <span className="text-[10px] leading-none">🇨🇦</span>
          </div>
          <h3 className="font-heading text-lg uppercase tracking-tight text-foreground leading-tight">
            {subtitle}
          </h3>
          {!isStarting ? (
            <p className="text-xs text-cyan-100/80 leading-relaxed mt-1">
              <span className="font-bold text-cyan-200">{data.verified_meals}/{data.total_meals}</span>{' '}
              {t.of_meals} {t.nudge_verified}
            </p>
          ) : (
            <p className="text-xs text-cyan-100/80 leading-relaxed mt-1">
              {t.nudge_starting}
            </p>
          )}
        </div>
      </div>
    </motion.div>
  );
}
